import { Crown, Ellipsis, LogOut, Share, UserMinus, UserPlus, UserRound, X } from 'lucide-react'
import { useViewState } from '../lib/navigation'
import { ApiError } from '../api/client'
import { useCreateInvite, useRemoveMember, useSetRole, useTeam } from '../api/queries'
import type { Invite, Role, User } from '../api/types'
import { useAuth, useUser } from '../auth/context'
import { Avatar } from '../components/Avatar'
import { Button } from '../components/Button'
import { CopyField } from '../components/CopyField'
import { Dialog } from '../components/Dialog'
import { PageHeader } from '../components/PageHeader'
import { Card, Section } from '../components/Section'
import { Spinner } from '../components/Spinner'
import { toast } from '../components/Toast'
import { shareToTelegram } from '../lib/download'
import { formatDateTime } from '../lib/format'
import { memberActions, roleLabel, teamErrorText } from '../lib/team'
import { useIsDesktop } from '../lib/useIsDesktop'
import { isInTelegram } from '../telegram'

function teamError(err: unknown): string {
  return err instanceof ApiError ? teamErrorText(err.status, err.detail) : teamErrorText(null, null)
}

export function SettingsPage() {
  const auth = useUser()
  const { signOut } = useAuth()
  // Роль из списка команды свежее, чем из входа: её мог поменять другой владелец.
  const team = useTeam()
  const user = team.data?.find((m) => m.telegram_id === auth.telegram_id) ?? auth

  return (
    <div className="mx-auto max-w-2xl desk:mx-0">
      <PageHeader title="Настройки" />

      <Card className="flex items-center gap-3 bg-surface! p-4">
        <Avatar name={user.name} src={user.photo_url} size={52} />
        <div className="min-w-0 flex-1">
          <div className="truncate text-base font-semibold">{user.name}</div>
          <div className="truncate text-sm text-muted">
            {user.username ? `@${user.username} · ` : ''}
            {roleLabel(user)}
          </div>
        </div>
      </Card>

      <Team me={user} />

      {!isInTelegram() && (
        <Section title="Аккаунт" className="mt-6">
          <Card>
            <button
              type="button"
              aria-label="Выйти"
              onClick={signOut}
              className="flex min-h-12 w-full items-center gap-3 rounded-xl px-4 py-2.5 text-left hover:bg-surface"
            >
              <LogOut size={18} className="shrink-0 text-link" aria-hidden />
              <span>
                <span className="block text-sm font-semibold">Выйти</span>
                <span className="block text-xs text-muted">Завершить сеанс</span>
              </span>
            </button>
          </Card>
        </Section>
      )}
    </div>
  )
}

type TeamDialog = { kind: 'sheet' | 'role' | 'remove'; member: User }

function Team({ me }: { me: User }) {
  const team = useTeam()
  const invite = useCreateInvite()
  const [dialogRef, setDialogRef] = useViewState<{ kind: TeamDialog['kind']; id: number } | null>('settings.dialog', null)
  const setDialog = (value: TeamDialog | null) => setDialogRef(value ? { kind: value.kind, id: value.member.telegram_id } : null)
  const member = team.data?.find((item) => item.telegram_id === dialogRef?.id)
  const dialog: TeamDialog | null = dialogRef && member ? { kind: dialogRef.kind, member } : null
  // Владелец приглашает, убирает и меняет роли и в браузере, и в Mini App (docs/product.md, «Где что живёт»).
  const canManage = me.role === 'owner'
  const close = () => setDialog(null)

  return (
    <Section
      title={team.data ? `Команда · ${team.data.length}` : 'Команда'}
      className="mt-6"
      aside={
        canManage && (
          <Button size="sm" variant="primary" disabled={invite.isPending} onClick={() => invite.mutate()}>
            {invite.isPending ? <Spinner size={14} className="text-accent-fg" /> : <UserPlus size={14} />}
            Пригласить
          </Button>
        )
      }
    >
      {invite.isError && <p className="mb-2 text-sm text-danger">Не получилось создать приглашение.</p>}
      {invite.data && <InvitePanel invite={invite.data} onClose={() => invite.reset()} />}

      <Card className="overflow-hidden">
        {team.isPending ? (
          <div className="flex justify-center p-6">
            <Spinner />
          </div>
        ) : team.isError ? (
          <div className="flex items-center justify-between gap-3 p-4 text-sm">
            <span className="text-muted">Не удалось загрузить команду.</span>
            <Button size="sm" onClick={() => void team.refetch()}>
              Повторить
            </Button>
          </div>
        ) : (
          <ul className="divide-y divide-line">
            {team.data.map((member) => (
              <MemberRow
                key={member.telegram_id}
                member={member}
                me={me}
                onAction={(kind) => setDialog({ kind, member })}
              />
            ))}
          </ul>
        )}
      </Card>

      {dialog?.kind === 'sheet' && (
        <MemberSheet member={dialog.member} me={me} onAction={(kind) => setDialog({ kind, member: dialog.member })} onClose={close} />
      )}
      {dialog?.kind === 'role' && <RoleDialog member={dialog.member} onClose={close} />}
      {dialog?.kind === 'remove' && <RemoveDialog member={dialog.member} onClose={close} />}
    </Section>
  )
}

function InvitePanel({ invite, onClose }: { invite: Invite; onClose: () => void }) {
  // Ссылку на Mini App удобнее всего переслать прямо в Telegram: «Поделиться» открывает выбор чата.
  const telegram = invite.url_miniapp && (
    <CopyField
      key="tg"
      label="Для Telegram"
      value={invite.url_miniapp}
      extra={
        <Button
          onClick={() =>
            shareToTelegram(invite.url_miniapp ?? '', 'Приглашение в Контент Завод: откройте ссылку и войдите через Telegram')
          }
        >
          <Share size={16} />
          Поделиться
        </Button>
      }
    />
  )
  const web = <CopyField key="web" label="Для браузера" value={invite.url_web} />
  return (
    <Card className="mb-3 p-4">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <div className="font-medium">Ссылка-приглашение</div>
          <div className="text-sm text-muted">Действует до {formatDateTime(invite.expires_at)}</div>
        </div>
        <Button size="icon" variant="ghost" aria-label="Скрыть" onClick={onClose} className="-mt-2 -mr-2">
          <X size={16} />
        </Button>
      </div>
      <div className="space-y-3">{isInTelegram() ? [telegram, web] : [web, telegram]}</div>
    </Card>
  )
}

function RoleBadge({ member }: { member: User }) {
  const owner = member.role === 'owner'
  return (
    <span
      className={`inline-flex h-5 shrink-0 items-center gap-1 rounded-md px-1.5 text-xs leading-none font-medium ring-1 ring-line ring-inset ${
        owner ? 'bg-surface text-fg' : 'text-muted'
      }`}
    >
      {owner && <Crown size={11} aria-hidden />}
      {roleLabel(member)}
    </span>
  )
}

const SET_ROLE_LABEL: Record<Role, string> = { owner: 'Сделать владельцем', member: 'Сделать участником' }

/**
 * Строка человека. На компьютере действия — кнопками в строке, на телефоне и в
 * Mini App — «⋯» открывает лист с теми же действиями, как у источников.
 */
function MemberRow({ member, me, onAction }: { member: User; me: User; onAction: (kind: TeamDialog['kind']) => void }) {
  const desktop = useIsDesktop()
  const actions = memberActions(me, member)
  const isMe = member.telegram_id === me.telegram_id

  return (
    <li className="flex min-h-16 items-center gap-3 px-4 py-2.5 desk:min-h-13">
      <Avatar name={member.name} src={member.photo_url} size={36} />
      <div className="min-w-0 flex-1">
        <div className="truncate font-medium">
          {member.name}
          {isMe && <span className="font-normal text-muted"> · вы</span>}
        </div>
        <div className="mt-0.5 flex min-w-0 items-center gap-1.5 text-sm text-muted">
          <RoleBadge member={member} />
          {member.username && <span className="truncate">@{member.username}</span>}
        </div>
      </div>
      {(actions.setRole || actions.remove) &&
        (desktop ? (
          <div className="-mr-1 flex shrink-0 items-center gap-1">
            {actions.setRole && (
              <Button size="sm" variant="ghost" onClick={() => onAction('role')}>
                {SET_ROLE_LABEL[actions.setRole]}
              </Button>
            )}
            {actions.remove && (
              <Button size="sm" variant="ghost" onClick={() => onAction('remove')}>
                Убрать
              </Button>
            )}
          </div>
        ) : (
          <Button size="icon" variant="ghost" className="-mr-2" aria-label={`Действия: ${member.name}`} onClick={() => onAction('sheet')}>
            <Ellipsis size={18} />
          </Button>
        ))}
    </li>
  )
}

const OWNER_RIGHTS = 'приглашать и убирать людей, менять им роли, убирать источники и архивировать доски плана'

function MemberSheet({
  member,
  me,
  onAction,
  onClose,
}: {
  member: User
  me: User
  onAction: (kind: 'role' | 'remove') => void
  onClose: () => void
}) {
  const actions = memberActions(me, member)
  return (
    <Dialog title={member.name} onClose={onClose}>
      <div className="flex items-center gap-2 text-sm text-muted">
        <RoleBadge member={member} />
        {member.username && <span className="truncate">@{member.username}</span>}
      </div>
      <p className="mt-2 text-sm text-pretty">
        {member.role === 'owner' ? `Может ${OWNER_RIGHTS}.` : `Не может ${OWNER_RIGHTS}. Остальное доступно.`}
      </p>
      <div className="mt-4 flex flex-col gap-2">
        {actions.setRole && (
          <Button onClick={() => onAction('role')}>
            {actions.setRole === 'owner' ? <Crown size={16} /> : <UserRound size={16} />}
            {SET_ROLE_LABEL[actions.setRole]}
          </Button>
        )}
        {actions.remove && (
          <Button variant="ghost" onClick={() => onAction('remove')}>
            <span className="inline-flex items-center gap-2 text-danger">
              <UserMinus size={16} />
              Убрать из команды
            </span>
          </Button>
        )}
      </div>
    </Dialog>
  )
}

function RoleDialog({ member, onClose }: { member: User; onClose: () => void }) {
  const setRole = useSetRole()
  const role: Role = member.role === 'owner' ? 'member' : 'owner'
  const promote = role === 'owner'
  return (
    <Dialog
      title={promote ? 'Сделать владельцем?' : 'Сделать участником?'}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button
            variant="primary"
            disabled={setRole.isPending}
            onClick={() =>
              setRole.mutate(
                { telegramId: member.telegram_id, role },
                {
                  onSuccess: () => {
                    toast(promote ? `${member.name} теперь владелец` : `${member.name} теперь участник`)
                    onClose()
                  },
                },
              )
            }
          >
            {setRole.isPending && <Spinner size={14} className="text-accent-fg" />}
            {SET_ROLE_LABEL[role]}
          </Button>
        </>
      }
    >
      <p className="text-sm text-pretty">
        {promote
          ? `${member.name} сможет ${OWNER_RIGHTS}.`
          : `${member.name} больше не сможет ${OWNER_RIGHTS}. Остальное останется как было.`}
      </p>
      {promote && (
        <p className="mt-2 text-sm text-pretty text-muted">
          Права можно забрать в любой момент. Тревоги об авариях по-прежнему приходят только главному владельцу.
        </p>
      )}
      {setRole.isError && <p className="mt-2 text-sm text-danger">{teamError(setRole.error)}</p>}
    </Dialog>
  )
}

function RemoveDialog({ member, onClose }: { member: User; onClose: () => void }) {
  const remove = useRemoveMember()
  return (
    <Dialog
      title="Убрать из команды?"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button
            variant="danger"
            disabled={remove.isPending}
            onClick={() =>
              remove.mutate(member.telegram_id, {
                onSuccess: () => {
                  toast('Убрали из команды')
                  onClose()
                },
              })
            }
          >
            {remove.isPending && <Spinner size={14} className="text-white" />}
            Убрать
          </Button>
        </>
      }
    >
      <p className="text-sm text-pretty">
        {member.name} потеряет доступ к Заводу и перестанет получать сводку в 10:00. Вернуть можно новым приглашением.
      </p>
      {remove.isError && <p className="mt-2 text-sm text-danger">{teamError(remove.error)}</p>}
    </Dialog>
  )
}
